import { useState } from 'react';
import { ChevronDown, Plus, HelpCircle } from 'lucide-react';

const FAQAccordion = ({ faqsByCategory, onCreateTicket }) => {
  const [openId, setOpenId] = useState(null);

  // Helper to check if a category has items
  const hasFaqs = Object.values(faqsByCategory).some(list => list.length > 0);

  if (!hasFaqs) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-800 mb-4">
          <HelpCircle className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">No FAQs found</h3>
        <p className="text-gray-500 dark:text-gray-400">Try selecting a different category.</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {Object.entries(faqsByCategory).map(([category, faqs]) => {
        if (faqs.length === 0) return null;

        return (
          <div key={category} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Category Title with Decorative Line */}
            <div className="flex items-center gap-3 mb-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">
                {category}
              </h3>
              <div className="h-px w-full bg-gray-200 dark:bg-slate-800 rounded-full" />
            </div>

            <div className="space-y-3">
              {faqs.map((faq) => {
                const isOpen = openId === faq.id;

                return (
                  <div
                    key={faq.id}
                    className={`
                      group rounded-xl border transition-all duration-300 overflow-hidden
                      ${isOpen 
                        ? 'bg-white dark:bg-slate-800 border-indigo-200 dark:border-indigo-500/30 shadow-lg shadow-indigo-500/10 ring-1 ring-indigo-500/20' 
                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-slate-700'
                      }
                    `}
                  >
                    <button
                      onClick={() => setOpenId(isOpen ? null : faq.id)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left"
                    >
                      <span
                        className={`font-semibold text-base transition-colors ${
                          isOpen
                            ? 'text-indigo-600 dark:text-indigo-400'
                            : 'text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white'
                        }`}
                      >
                        {faq.question}
                      </span>
                      <div
                        className={`p-1 rounded-full transition-all duration-300 ${
                          isOpen
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 rotate-180'
                            : 'bg-gray-100 dark:bg-slate-800 group-hover:bg-indigo-50 dark:group-hover:bg-slate-700'
                        }`}
                      >
                        <ChevronDown
                          className={`w-5 h-5 transition-colors ${
                            isOpen ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'
                          }`}
                        />
                      </div>
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-6 pb-6 pt-0">
                          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4 border-l-2 border-indigo-100 dark:border-indigo-900/50 pl-4">
                            {faq.answer}
                          </p>

                          <button
                            onClick={() => onCreateTicket({ subject: faq.question, description: faq.answer })}
                            className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group/btn"
                          >
                            <div className="p-1 rounded-md bg-indigo-50 dark:bg-indigo-900/20 group-hover/btn:bg-indigo-100 dark:group-hover/btn:bg-indigo-900/40 transition-colors">
                              <Plus className="w-3.5 h-3.5" />
                            </div>
                            Create ticket from this answer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQAccordion;
